import { CommonModule, DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { forkJoin, Subscription } from 'rxjs';
import { DialogComponent } from '@sml/dialog';
import { IconsComponent } from '@sml/icons';
import { SpinnerComponent } from '@sml/spinner';
import {
  IStockImage,
  IStockImageSearchResult,
  PlatformService,
  STOCK_IMAGE_PAGE_SIZE,
  STOCK_IMAGE_STRINGS,
  StockImageAspectRatio,
  StockImageService,
} from '@sml/utility';
import {
  resolveStockImageDefaultQuery,
  resolveStockImageKeywords,
  STOCK_IMAGE_PICKER_STRINGS,
  STOCK_IMAGE_TILE_GRID_CLASSES,
  STOCK_IMAGE_TILE_SHAPE_CLASSES,
  StockImagePickerContext,
} from './stock-image-picker.constant';

@Component({
  selector: 'sml-stock-image-picker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogComponent,
    IconsComponent,
    SpinnerComponent,
  ],
  templateUrl: './stock-image-picker.component.html',
  styleUrls: ['./stock-image-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StockImagePickerComponent implements OnDestroy {
  private readonly stockImageService = inject(StockImageService);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly document = inject(DOCUMENT);
  private readonly platformService = inject(PlatformService);
  private isHostMovedToBody = false;
  /**
   * In-flight requests are held so closing the dialog can cancel them. Without
   * that, a response arriving after the reset would repopulate a dialog the
   * user has already closed.
   */
  private searchSubscription: Subscription | null = null;
  private loadMoreSubscription: Subscription | null = null;
  private addSubscription: Subscription | null = null;

  isOpen = input(false);
  /** False restricts the user to one photo, matching a single-image field. */
  allowMultiple = input(true);
  aspectRatio = input<StockImageAspectRatio>('original');
  /** Where the picker was opened from. Shapes the keyword tags and the search run on open. */
  context = input<StockImagePickerContext>('general');
  campaignType = input<number | null>(null);

  /** Emits the chosen photos as files, ready for the normal upload path. */
  photosPicked = output<File[]>();
  closed = output<void>();
  strings = STOCK_IMAGE_PICKER_STRINGS;
  searchTerm = '';
  activeQuery = signal('');
  isDefaultSearch = signal(false);
  images = signal<IStockImage[]>([]);
  selected = signal<IStockImage[]>([]);
  previewImage = signal<IStockImage | null>(null);
  previewLoaded = signal(false);
  isSearching = signal(false);
  isLoadingMore = signal(false);
  isAdding = signal(false);
  hasSearched = signal(false);
  hasMore = signal(false);
  errorMessage = signal('');
  private page = signal(1);
  isConfigured = this.stockImageService.isConfigured();
  providerName = this.stockImageService.activeProvider.displayName;
  notConfiguredMessage = STOCK_IMAGE_STRINGS.NOT_CONFIGURED;
  requiresAttribution =
    this.stockImageService.activeProvider.requiresAttribution ?? false;
  attributionUrl = this.stockImageService.activeProvider.attributionUrl ?? '';
  tileShapeClass = computed(
    () => STOCK_IMAGE_TILE_SHAPE_CLASSES[this.aspectRatio()],
  );
  tileGridClass = computed(
    () => STOCK_IMAGE_TILE_GRID_CLASSES[this.aspectRatio()],
  );
  suggestedKeywords = computed(() =>
    resolveStockImageKeywords(this.context(), this.campaignType() || null),
  );

  /**
   * The search the dialog opens on, so it never opens on an empty grid. Results
   * are cached per query, so reopening the dialog costs the provider nothing.
   */
  defaultQuery = computed(() =>
    resolveStockImageDefaultQuery(this.context(), this.campaignType() || null),
  );

  /**
   * The search that runs as the dialog opens. It is the only one the user did
   * not ask for, so it gets the full screen spinner rather than the panel one:
   * there is nothing else on the dialog worth looking at until it lands.
   */
  isLoadingFirstResults = computed(
    () => this.isSearching() && !this.hasSearched(),
  );

  selectedCount = computed(() => this.selected().length);
  canAdd = computed(() => this.selectedCount() > 0 && !this.isAdding());
  hasSelection = computed(() => this.selectedCount() > 0);

  previewFrameRatio = computed(() => {
    switch (this.aspectRatio()) {
      case 'square':
        return '1 / 1';
      case 'landscape':
        return '7 / 4';
      // Cover shows the whole photo, contained: it is cropped after picking.
      default:
        return null;
    }
  });

  previewPanelWidthClass = computed(() => {
    switch (this.aspectRatio()) {
      case 'square':
        return 'tw-max-w-[480px]';
      case 'landscape':
      case 'cover':
        return 'tw-max-w-[720px]';
      default:
        return 'tw-max-w-[640px]';
    }
  });

  selectionHint = computed(() =>
    this.allowMultiple()
      ? this.strings.MULTI_SELECT_HINT
      : this.strings.SINGLE_SELECT_HINT,
  );

  addButtonLabel = computed(() => {
    const count = this.selectedCount();
    return count > 1
      ? this.strings.ADD_MULTIPLE.replace('{count}', `${count}`)
      : this.strings.ADD_SINGLE;
  });

  constructor() {
    effect(() => {
      if (this.isOpen()) {
        this.moveHostToBody();
        untracked(() => this.showDefaultResults());
        return;
      }
      this.reset();
    });
  }

  ngOnDestroy(): void {
    this.cancelRequests();
    const host = this.elementRef.nativeElement as HTMLElement;
    if (this.isHostMovedToBody && host.parentNode === this.document.body) {
      host.remove();
    }
  }

  private moveHostToBody(): void {
    if (this.isHostMovedToBody || !this.platformService.browser()) return;
    this.document.body.appendChild(this.elementRef.nativeElement);
    this.isHostMovedToBody = true;
  }

  private reset(): void {
    this.cancelRequests();
    // Image bytes only need to outlive a selection, so they go when the dialog
    // does. The cached search results stay - they are metadata, and they are
    // what keeps reopening the library off the provider's request limit.
    this.stockImageService.releaseDownloads();
    this.searchTerm = '';
    this.activeQuery.set('');
    this.isDefaultSearch.set(false);
    this.images.set([]);
    this.selected.set([]);
    this.previewImage.set(null);
    this.previewLoaded.set(false);
    this.hasSearched.set(false);
    this.hasMore.set(false);
    this.errorMessage.set('');
    this.isSearching.set(false);
    this.isLoadingMore.set(false);
    this.isAdding.set(false);
    this.page.set(1);
  }

  private cancelRequests(): void {
    this.searchSubscription?.unsubscribe();
    this.loadMoreSubscription?.unsubscribe();
    this.addSubscription?.unsubscribe();
    this.searchSubscription = null;
    this.loadMoreSubscription = null;
    this.addSubscription = null;
  }

  private showDefaultResults(): void {
    const query = this.defaultQuery();
    if (!query || !this.isConfigured || this.hasSearched()) return;
    this.isDefaultSearch.set(true);
    this.runSearch(query);
  }

  isKeywordActive(keyword: string): boolean {
    if (this.isDefaultSearch()) return false;
    return this.activeQuery().toLowerCase() === keyword.toLowerCase();
  }

  clearSelection(): void {
    this.selected.set([]);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.isOpen()) return;
    if (this.previewImage()) {
      this.closePreview();
      return;
    }
    this.close();
  }

  tileUrl(image: IStockImage): string {
    return this.stockImageService.croppedThumbnailUrl(image, this.aspectRatio());
  }

  previewUrlFor(image: IStockImage): string {
    return this.stockImageService.croppedPreviewUrl(image, this.aspectRatio());
  }

  onImageFailed(image: IStockImage): void {
    this.images.update((current) =>
      current.filter((candidate) => candidate.id !== image.id),
    );
    this.selected.update((current) =>
      current.filter((candidate) => candidate.id !== image.id),
    );
    if (this.previewImage()?.id === image.id) {
      this.previewImage.set(null);
    }
  }

  isSelected(image: IStockImage): boolean {
    return this.selected().some((selected) => selected.id === image.id);
  }

  search(): void {
    const query = this.searchTerm.trim();
    if (!query || !this.isConfigured) return;
    this.isDefaultSearch.set(false);
    this.runSearch(query);
  }

  private runSearch(query: string): void {
    this.activeQuery.set(query);
    this.page.set(1);
    this.isSearching.set(true);
    this.errorMessage.set('');
    this.searchSubscription?.unsubscribe();
    this.searchSubscription = this.stockImageService
      .search(query, 1, STOCK_IMAGE_PAGE_SIZE, this.aspectRatio())
      .subscribe({
        next: (result: IStockImageSearchResult) => {
          this.images.set(result.images);
          this.hasMore.set(result.hasMore);
          this.hasSearched.set(true);
          this.isSearching.set(false);
        },
        error: (error: Error) => {
          this.images.set([]);
          this.hasSearched.set(true);
          this.isSearching.set(false);
          this.errorMessage.set(
            error.message || STOCK_IMAGE_STRINGS.SEARCH_FAILED,
          );
        },
      });
  }

  searchKeyword(keyword: string): void {
    this.searchTerm = keyword;
    this.search();
  }

  loadMore(): void {
    if (this.isLoadingMore() || !this.hasMore()) return;
    const nextPage = this.page() + 1;
    this.isLoadingMore.set(true);
    this.loadMoreSubscription = this.stockImageService
      .search(
        this.activeQuery(),
        nextPage,
        STOCK_IMAGE_PAGE_SIZE,
        this.aspectRatio(),
      )
      .subscribe({
        next: (result: IStockImageSearchResult) => {
          this.images.update((current) => [...current, ...result.images]);
          this.hasMore.set(result.hasMore);
          this.page.set(nextPage);
          this.isLoadingMore.set(false);
        },
        error: (error: Error) => {
          this.isLoadingMore.set(false);
          this.errorMessage.set(
            error.message || STOCK_IMAGE_STRINGS.SEARCH_FAILED,
          );
        },
      });
  }

  toggleSelection(image: IStockImage): void {
    if (!this.allowMultiple()) {
      const picked = !this.isSelected(image);
      this.selected.set(picked ? [image] : []);
      // Single select means one photo is confirmed at most, so its download
      // can start now rather than on the Add button - a full size photo off
      // the CDN is otherwise seconds of waiting with the dialog sitting there.
      if (picked) {
        this.stockImageService.prefetch(image, this.aspectRatio());
      }
      return;
    }
    this.selected.update((current) =>
      current.some((selected) => selected.id === image.id)
        ? current.filter((selected) => selected.id !== image.id)
        : [...current, image],
    );
  }

  /**
   * Picking from the preview finishes the job the preview was opened for, so the
   * overlay steps out of the way and leaves the grid showing the new selection.
   * Unpicking keeps it open, so the button can show it went back to 'Add'.
   */
  toggleSelectionFromPreview(image: IStockImage): void {
    this.toggleSelection(image);
    if (this.isSelected(image)) this.closePreview();
  }

  openPreview(image: IStockImage, event: MouseEvent): void {
    event.stopPropagation();
    this.previewLoaded.set(false);
    this.previewImage.set(image);
  }

  onPreviewLoaded(): void {
    this.previewLoaded.set(true);
  }

  closePreview(): void {
    this.previewImage.set(null);
    this.previewLoaded.set(false);
  }

  addSelected(): void {
    const selected = this.selected();
    if (!selected.length || this.isAdding()) return;

    this.isAdding.set(true);
    this.errorMessage.set('');

    this.addSubscription = forkJoin(
      selected.map((image) =>
        this.stockImageService.downloadAsFile(image, this.aspectRatio()),
      ),
    ).subscribe({
      next: (files: File[]) => {
        this.isAdding.set(false);
        this.photosPicked.emit(files);
        this.close();
      },
      error: (error: Error) => {
        this.isAdding.set(false);
        this.errorMessage.set(
          error.message || STOCK_IMAGE_STRINGS.DOWNLOAD_FAILED,
        );
      },
    });
  }

  close(): void {
    this.reset();
    this.closed.emit();
  }
}
